import { useQuery } from '@hooks/reactQuery';

import { getOptionsGranularities } from '@api/kpi';

import { extractQueryParams } from '@utils/common';
import { getDateSelectionParameters } from '@components/Kpi/Parameters/utils';

import { TNameTitle } from '@interfaces/common/common';
import { IGranularitiesOptionsDTO } from '@interfaces/kpi/dto/kpiDTO';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EUserRoles } from '@enums/common';
import { EDateSelectionModes, EDynamicDateRange } from '@enums/kpi-api';
import { EGranularityValue } from '@enums/kpi-common';

import { refetchOptions } from '@constants/common';
import { OPTIONS_GRANULARITIES } from '@constants/queryKeys';

interface IUseQueryOptionsGranularitiesReturned {
	optionsGranularities: TSelectOptionType<EGranularityValue>[];
	isLoadingOptionsGranularities: boolean;
}

interface IUseQueryOptionsGranularities extends IGranularitiesOptionsDTO {
	isOranVendor: boolean;
}

export const useQueryOptionsGranularities = (
	props: IUseQueryOptionsGranularities,
): IUseQueryOptionsGranularitiesReturned => {
	const {
		isOranVendor,
		vendor,
		from,
		to,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	} = props;

	const isValidDatesForGranularities = dateSelectionMode === EDateSelectionModes.static
		? (!!from && !!to)
		: dynamicDateRange?.value === EDynamicDateRange.custom
			? startDateOffset != null
			: !!dynamicDateRange;

	const dateSelectionParameters = getDateSelectionParameters({
		isOranVendor,
		values: {
			dateSelectionMode,
			dynamicDateRange,
			startDateOffset,
		},
		dateFromUTC: from,
		dateToUTC: to,
	});

	const {
		data: optionsGranularities,
		isInitialLoading,
		isFetching,
	} = useQuery<TNameTitle<EGranularityValue>[], TSelectOptionType<EGranularityValue>[]>({
		queryKey: [OPTIONS_GRANULARITIES, {
			vendor,
			isOranVendor,
			...dateSelectionParameters,
		}],
		queryFn: extractQueryParams(getOptionsGranularities),
		allowedRoles: [EUserRoles.networkOperatorKpiUpdater, EUserRoles.networkOperatorKpiViewer],
		select: (data) => data?.map((item) => ({
			value: item.name,
			label: item.title,
		})),
		enabled: [
			isOranVendor,
			vendor,
			isValidDatesForGranularities,
		],
		...refetchOptions,
	});

	return {
		optionsGranularities: optionsGranularities || [],
		isLoadingOptionsGranularities: isFetching || isInitialLoading,
	};
};
